import React, { useCallback, useEffect, useRef, useState } from 'react';
import OfficeIdCard, { DEFAULT_CARD_LAYOUT } from './OfficeIdCard';
import PageContainer from '../../../components/container/PageContainer';
import FeatherIcon from 'feather-icons-react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  TextField,
  ListSubheader,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { GetAllEmployeeDetailsDropdowns, GetEmployeeCardDetails } from '../../../services/human-resource';
import { GetUserDetail } from '../../../services/user';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const AUTO_ROTATION_SPEED = 28; // degrees per second
const MANUAL_FLIP_DURATION = 700;

const initialData = {
  empName: 'Bhargav Shukla',
  empId: '8063',
  dob: '28-10-1999',
  doj: '22-03-2023',
  doi: '22-03-2023',
  bloodGroup: 'AB+VE',
  empPhotoUrl: 'https://hrms.sswhite.co/pic/8063.png',
  signatureUrl: '/admin-sign.png',
  logoUrl: '/sswhite-icard-img.jpg',
  companyTitleFront: 'S. S. WHITE TECHNOLOGIES',
  companySubtitleFront: 'INDIA PVT LTD',
  companyTitleBack: 'S.S. WHITE TECHNOLOGIES INDIA PVT LTD',
  companyAddressFront:
    'Survey No. 2788/3, Wadhwan-Ahmedabad Highway, Near Krishna Petrol Pump, Wadhwan-363030',
  companyAddressBack:
    'Survey No. 2788/3, Wadhwan-Ahmedabad Highway, Near Krishna Petrol Pump, Wadhwan-363030, Dist: Surendranagar, Gujarat India',
  emergencyPhone: '02752 287521',
  emergencyMobile: '+91 7698001501',
  companySubtitleBold: 'Emergency Contact No',
  signatureUrlBack: '/signature.png',
};

const getEmployeePhotoUrl = (picture, personId) => {
  if (!picture && !personId) return "";

  return picture
    ? `https://dashboardapi.sswhite.co/SSWhiteImages/${picture}`
    : `https://hrms.sswhite.co/pic/${personId}.png`;
};

const getEmployeeDetailRows = (response) => {
  const responseData = response?.data;

  if (Array.isArray(responseData)) {
    return responseData;
  }

  return responseData?.employeeDetails || [];
};



const NumericAdjuster = ({
  label,
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
}) => {
  // Keep these controls intentionally simple: one click = exactly one step.
  // This avoids React re-rendering while a pointer-repeat timer is running.
  const change = (direction) => {
    const current = Number(value) || 0;
    const next = Math.max(min, Math.min(max, current + direction * step));
    if (next !== current) onChange(next);
  };

  return (
    <Box
      sx={{
        border: '1px solid #cbd5e1',
        borderRadius: 1.5,
        background: '#fff',
        overflow: 'hidden',
        minHeight: 56,
      }}
    >
      <Typography
        sx={{
          px: 1.25,
          pt: 0.55,
          fontSize: 11,
          color: '#64748b',
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 0.55,
          pb: 0.45,
          pt: 0.25,
        }}
      >
        <Button
          size='small'
          onClick={() => change(-1)}
          sx={{
            minWidth: 34,
            width: 34,
            height: 30,
            p: 0,
            borderRadius: 1,
            fontSize: 20,
            lineHeight: 1,
            color: '#475569',
            border: '1px solid #e2e8f0',
            '&:hover': { background: '#f1f5f9' },
            '&:active': { transform: 'scale(.96)' },
          }}
        >
          −
        </Button>

        <Typography
          sx={{
            minWidth: 54,
            textAlign: 'center',
            fontSize: 14,
            fontWeight: 700,
            color: '#0f172a',
            userSelect: 'none',
          }}
        >
          {Math.round(Number(value) || 0)}
        </Typography>

        <Button
          size='small'
          onClick={() => change(1)}
          sx={{
            minWidth: 34,
            width: 34,
            height: 30,
            p: 0,
            borderRadius: 1,
            fontSize: 20,
            lineHeight: 1,
            color: '#475569',
            border: '1px solid #e2e8f0',
            '&:hover': { background: '#f1f5f9' },
            '&:active': { transform: 'scale(.96)' },
          }}
        >
          +
        </Button>
      </Box>
    </Box>
  );
};


export default function OfficeIdCardPage() {
  // Auto rotation only controls whether the requestAnimationFrame loop runs.
  // The current angle stays in a ref, so the full page does not re-render
  // on every animation frame.
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  // --- Bulk flip state ---
  const [bulkFlipStates, setBulkFlipStates] = useState({});
  const [exporting, setExporting] = useState(false);
  // --- Original states ---
  const [mode, setMode] = useState('single');
  const [dropdownEmployees, setDropdownEmployees] = useState([]);
  const [cardData, setCardData] = useState(initialData);
  const [bulkCardData, setBulkCardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // --- Optional card designer (normal mode remains unchanged) ---
  const [designMode, setDesignMode] = useState(false);
  const [designSide, setDesignSide] = useState('front');
  const [selectedDesignElement, setSelectedDesignElement] = useState(null);
  const [cardLayout, setCardLayout] = useState(() => {
    try {
      const saved = localStorage.getItem('office-id-card-layout-v1');
      return saved ? JSON.parse(saved) : DEFAULT_CARD_LAYOUT;
    } catch {
      return DEFAULT_CARD_LAYOUT;
    }
  });
  const designDragRef = useRef(null);
  const designRafRef = useRef(null);
  const designPointerRef = useRef(null);

  useEffect(() => {
    setCardLayout((prev) => {
      if (!prev?.front) return prev;
      const f = prev.front;
      const hasSplit = f.authSignImage && f.authSignLabel;
      if (hasSplit) return prev;
      const legacy = f.authSign || DEFAULT_CARD_LAYOUT.front.authSign;
      return {
        ...prev,
        front: {
          ...f,
          authSignImage: { x: legacy.x, y: legacy.y, w: legacy.w, h: Math.max(20, legacy.h - 19) },
          authSignLabel: { x: legacy.x, y: legacy.y + Math.max(20, legacy.h - 19), w: legacy.w, h: 19, fontSize: legacy.fontSize || 16 },
        },
      };
    });
  }, []);

  const DESIGN_ELEMENT_NAMES = {
    watermark: 'Watermark',
    header: 'Header / Logo',
    photo: 'Employee Photo',
    name: 'Employee Name',
    address: 'Address',
    dob: 'Date of Birth',
    doj: 'Date of Joining',
    doi: 'Date of Issue',
    bloodGroup: 'Blood Group',
    idBadge: 'ID Badge',
    authSignImage: 'Signature Image',
    authSignLabel: 'Signature Label',
    authSign: 'Authorized Signature',
    empSign: 'Employee Signature',
    emergency: 'Emergency Title',
    phone: 'Phone',
    mobile: 'Mobile',
  };

  const saveCardLayout = (nextLayout = cardLayout) => {
    try {
      localStorage.setItem('office-id-card-layout-v1', JSON.stringify(nextLayout));
    } catch (error) {
      console.error('Unable to save card layout:', error);
    }
  };

  const updateDesignItem = (side, id, patch) => {
    setCardLayout((prev) => {
      const next = {
        ...prev,
        [side]: {
          ...prev[side],
          [id]: {
            ...prev[side][id],
            ...patch,
          },
        },
      };
      return next;
    });
  };

  const getDesignItem = () =>
    selectedDesignElement
      ? cardLayout?.[designSide]?.[selectedDesignElement] || null
      : null;

  const getDetailsSettings = () => ({
    ...DEFAULT_CARD_LAYOUT.front.details,
    ...(cardLayout?.front?.details || {}),
  });

  const updateDetailsSettings = (patch) => {
    setCardLayout((prev) => ({
      ...prev,
      front: {
        ...prev.front,
        details: {
          ...DEFAULT_CARD_LAYOUT.front.details,
          ...(prev.front?.details || {}),
          ...patch,
        },
      },
    }));
  };

  const alignDetailRows = (overrides = {}) => {
    setCardLayout((prev) => {
      const details = {
        ...DEFAULT_CARD_LAYOUT.front.details,
        ...(prev.front?.details || {}),
        ...overrides,
      };
      const ids = ['address', 'dob', 'doj', 'doi', 'bloodGroup'];
      let currentY = Number(details.startY) || 330;
      const nextFront = { ...prev.front };

      ids.forEach((id, index) => {
        const current = nextFront[id] || {};
        nextFront[id] = {
          ...current,
          x: Number(details.x),
          w: Number(details.w),
          y: Math.round(currentY),
        };
        currentY += Number(current.h) || (id === 'address' ? 62 : 30);
        if (index < ids.length - 1) {
          currentY += Number(details.rowGap) || 0;
        }
      });

      return {
        ...prev,
        front: {
          ...nextFront,
          details,
        },
      };
    });
  };

  const commitDesignItem = useCallback((side, id, patch) => {
    setCardLayout((prev) => ({
      ...prev,
      [side]: {
        ...prev[side],
        [id]: {
          ...prev[side][id],
          ...patch,
        },
      },
    }));
  }, []);

  const applyDesignDom = useCallback((side, id, patch) => {
    const canvas = document.querySelector('.office-id-card-design-canvas');
    const element = canvas?.querySelector(`[data-layout-id="${side}:${id}"]`);
    if (!element) return;

    if (patch.x != null) element.style.left = `${patch.x}px`;
    if (patch.y != null) element.style.top = `${patch.y}px`;
    if (patch.w != null) element.style.width = `${patch.w}px`;
    if (patch.h != null) element.style.height = `${patch.h}px`;
  }, []);

  const beginDesignDrag = (event, id, mode) => {
    if (!designMode) return;
    event.preventDefault();
    event.stopPropagation();

    setSelectedDesignElement(id);

    const item = cardLayout?.[designSide]?.[id];
    if (!item) return;

    designDragRef.current = {
      id,
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: item.x,
      startY: item.y,
      startW: item.w,
      startH: item.h,
      side: designSide,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    };

    designPointerRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    };

    event.currentTarget?.setPointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    if (!designMode) return undefined;

    const move = (event) => {
      const drag = designDragRef.current;
      if (!drag) return;

      designPointerRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };

      if (designRafRef.current != null) return;

      designRafRef.current = requestAnimationFrame(() => {
        designRafRef.current = null;

        const currentDrag = designDragRef.current;
        const pointer = designPointerRef.current;
        if (!currentDrag || !pointer) return;

        const canvas = document.querySelector('.office-id-card-design-canvas');
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = rect.width / 380;
        const scaleY = rect.height / 640;
        const dx = (pointer.clientX - currentDrag.startClientX) / scaleX;
        const dy = (pointer.clientY - currentDrag.startClientY) / scaleY;

        let patch;

        if (currentDrag.mode === 'resize') {
          const nextW = Math.max(
            20,
            Math.min(380 - currentDrag.startX, currentDrag.startW + dx)
          );
          const nextH = Math.max(
            16,
            Math.min(640 - currentDrag.startY, currentDrag.startH + dy)
          );

          currentDrag.w = Math.round(nextW);
          currentDrag.h = Math.round(nextH);
          patch = { w: currentDrag.w, h: currentDrag.h };
        } else {
          const nextX = Math.max(
            0,
            Math.min(380 - currentDrag.startW, currentDrag.startX + dx)
          );
          const nextY = Math.max(
            0,
            Math.min(640 - currentDrag.startH, currentDrag.startY + dy)
          );

          currentDrag.x = Math.round(nextX);
          currentDrag.y = Math.round(nextY);
          patch = { x: currentDrag.x, y: currentDrag.y };
        }

        applyDesignDom(currentDrag.side, currentDrag.id, patch);
      });
    };

    const up = () => {
      if (designRafRef.current != null) {
        cancelAnimationFrame(designRafRef.current);
        designRafRef.current = null;
      }

      const drag = designDragRef.current;
      if (!drag) return;

      const patch =
        drag.mode === 'resize'
          ? { w: drag.w, h: drag.h }
          : { x: drag.x, y: drag.y };

      commitDesignItem(drag.side, drag.id, patch);
      designDragRef.current = null;
      designPointerRef.current = null;
    };

    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerup', up);

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (designRafRef.current != null) {
        cancelAnimationFrame(designRafRef.current);
        designRafRef.current = null;
      }
    };
  }, [applyDesignDom, commitDesignItem, designMode]);

  const resetCardLayout = () => {
    const reset = JSON.parse(JSON.stringify(DEFAULT_CARD_LAYOUT));
    setCardLayout(reset);
    saveCardLayout(reset);
    setSelectedDesignElement(null);
  };

  const closeDesignMode = () => {
    saveCardLayout(cardLayout);
    setDesignMode(false);
    setSelectedDesignElement(null);
  };

  // Search state for employee dropdown
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

  const companyAddress = "Survey No. 2788/3, Wadhwan-Ahmedabad Highway, Near Krishna Petrol Pump, Wadhwan-363030, Dist: Surendranagar, Gujarat India";
  const frontRef = useRef(null);
  const backRef = useRef(null);

  // Refs for the card flipper element (only used in single mode)
  const wrapperRef = useRef(null);
  const flipperRef = useRef(null);
  const searchInputRef = useRef(null);

  const rotationAngleRef = useRef(0);
  const animationFrameRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const manualFlipTimeoutRef = useRef(null);
  const isManualFlippingRef = useRef(false);

  useEffect(() => {
    loadEmployees();
    loadLoggedInEmployee();
  }, []);

  const applyRotation = useCallback((angle, animate = false) => {
    const flipper = flipperRef.current;

    if (!flipper) return;

    flipper.style.transition = animate
      ? `transform ${MANUAL_FLIP_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`
      : 'none';

    flipper.style.transform = `translateZ(0) rotateY(${angle}deg)`;
  }, []);

  const stopRotationFrame = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    lastTimestampRef.current = null;
  }, []);

  const clearManualFlipTimer = useCallback(() => {
    if (manualFlipTimeoutRef.current !== null) {
      clearTimeout(manualFlipTimeoutRef.current);
      manualFlipTimeoutRef.current = null;
    }

    isManualFlippingRef.current = false;
  }, []);

  const resetSingleRotation = useCallback(() => {
    stopRotationFrame();
    clearManualFlipTimer();

    rotationAngleRef.current = 0;
    lastTimestampRef.current = null;

    requestAnimationFrame(() => {
      applyRotation(0, false);
    });
  }, [applyRotation, clearManualFlipTimer, stopRotationFrame]);

  // Resolve the actual single-card flipper after the card mounts or changes.
  useEffect(() => {
    if (mode !== 'single') {
      flipperRef.current = null;
      return undefined;
    }

    const frameId = requestAnimationFrame(() => {
      const flipper = wrapperRef.current?.querySelector(
        '.office-id-card-flipper'
      );

      flipperRef.current = flipper || null;

      if (flipper) {
        flipper.style.willChange = 'transform';
        flipper.style.transformStyle = 'preserve-3d';
        flipper.style.webkitTransformStyle = 'preserve-3d';

        applyRotation(rotationAngleRef.current, false);
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [applyRotation, cardData.empId, mode]);

  // Smooth auto rotation. Only the transform style changes per frame;
  // React state is not updated during the animation.
  useEffect(() => {
    stopRotationFrame();

    if (!isAutoRotating || mode !== 'single') {
      return undefined;
    }

    clearManualFlipTimer();

    const animate = (timestamp) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
      }

      const elapsed = Math.min(
        timestamp - lastTimestampRef.current,
        50
      );

      lastTimestampRef.current = timestamp;

      rotationAngleRef.current +=
        AUTO_ROTATION_SPEED * (elapsed / 1000);

      // Keep the transform value reasonably small during long sessions.
      if (rotationAngleRef.current > 100000) {
        rotationAngleRef.current %= 360;
      }

      applyRotation(rotationAngleRef.current, false);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return stopRotationFrame;
  }, [
    applyRotation,
    clearManualFlipTimer,
    isAutoRotating,
    mode,
    stopRotationFrame,
  ]);

  // Bulk mode does not use the DOM-driven single-card rotation.
  useEffect(() => {
    if (mode !== 'bulk') return;

    stopRotationFrame();
    clearManualFlipTimer();

    setIsAutoRotating(false);
    rotationAngleRef.current = 0;
    lastTimestampRef.current = null;
    flipperRef.current = null;
  }, [clearManualFlipTimer, mode, stopRotationFrame]);

  // Cancel pending animation work when the page unmounts.
  useEffect(() => {
    return () => {
      stopRotationFrame();
      clearManualFlipTimer();
    };
  }, [clearManualFlipTimer, stopRotationFrame]);

  // Filter employees based on search term - search by name OR ID
  const getFilteredEmployees = () => {
    if (!employeeSearchTerm.trim()) return dropdownEmployees;
    const searchLower = employeeSearchTerm.toLowerCase().trim();
    return dropdownEmployees.filter(emp => {
      const nameMatch = emp.buddyName?.toLowerCase().includes(searchLower) || false;
      const idMatch = String(emp.personID).includes(searchLower);
      return nameMatch || idMatch;
    });
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);

      const { data } = await GetAllEmployeeDetailsDropdowns(true);

      setDropdownEmployees(data?.buddyDropDown || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const captureCardElement = async (element) => {
    const oldTransform = element.style.transform;
    const oldTransition = element.style.transition;

    element.style.transform = "none";
    element.style.transition = "none";

    const images = [...element.querySelectorAll("img")];

    await Promise.all(
      images.map(img =>
        img.complete
          ? Promise.resolve()
          : new Promise(resolve => {
            img.onload = resolve;
            img.onerror = resolve;
          })
      )
    );

    const canvas = await html2canvas(element, {
      scale: 4,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      logging: false,
      imageTimeout: 0,
    });

    element.style.transform = oldTransform;
    element.style.transition = oldTransition;

    return canvas;
  };

  const loadLoggedInEmployee = async () => {
    try {
      const userRes = await GetUserDetail();

      const personId = userRes.data.personId;

      const res = await GetEmployeeCardDetails(personId);

      const rows = getEmployeeDetailRows(res);
      const detail = rows[0];

      setSelectedEmployees([String(personId)]);
      const fullAddress = [detail?.add1, detail?.add2]
        .filter(Boolean)
        .join(", ");
      const data = {
        ...initialData,
        empName: `${userRes.data.firstName} ${userRes.data.lastName}`,
        empId: personId,
        dob: detail?.dob,
        doj: detail?.doj,
        doi: detail?.doi,
        bloodGroup: detail?.bloodGroup,
        empPhotoUrl: getEmployeePhotoUrl(
          detail?.picture,
          personId
        ),
        companyAddressFront: fullAddress,
        companyAddressBack: companyAddress,
      };

      setCardData(data);
      setBulkCardData([data]);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSelectedEmployee = async (employee) => {

    const res = await GetEmployeeCardDetails(employee.personID);

    const rows = getEmployeeDetailRows(res);
    const detail = rows[0];
    const fullAddress = [detail?.add1, detail?.add2]
      .filter(Boolean)
      .join(", ");
    const data = {
      ...initialData,

      empName: employee.buddyName,
      empId: employee.personID,

      dob: detail?.dob,
      doj: detail?.doj,
      doi: detail?.doi,

      bloodGroup: detail?.bloodGroup,

      empPhotoUrl: getEmployeePhotoUrl(
        detail?.picture,
        employee.personID
      ),

      companyAddressFront: fullAddress,
      companyAddressBack: companyAddress,
    };

    setCardData(data);
    setBulkCardData([data]);
  };

  const exportSinglePDF = async () => {
    try {
      setExporting(true);

      const container = wrapperRef.current;
      if (!container) {
        setExporting(false);
        return;
      }

      const frontCard = container.querySelector(".office-id-card-face:first-child");
      const backCard = container.querySelector(".office-id-card-face:last-child");

      if (!frontCard || !backCard) {
        setExporting(false);
        return;
      }

      const frontCanvas = await captureCardElement(frontCard);
      const backCanvas = await captureCardElement(backCard);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;

      const cardWidth = 70;          // smaller card
      const cardHeight = 118;        // maintains 380:640 aspect ratio

      const spacing = 8;

      const startX = (pageWidth - cardWidth) / 2;
      const totalHeight = (cardHeight * 2) + spacing;
      const startY = (pageHeight - totalHeight) / 2;

      pdf.addImage(frontCanvas, "PNG", startX, startY, cardWidth, cardHeight);

      pdf.addImage(
        backCanvas,
        "PNG",
        startX,
        startY + cardHeight + spacing,
        cardWidth,
        cardHeight
      );

      pdf.save(`Office_ID_${cardData.empName || cardData.empId || "Employee"}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };


  const exportBulkPDF = async () => {
    try {
      setExporting(true);
      // Force all cards to front before capture
      const frontStates = {};
      bulkCardData.forEach(card => {
        frontStates[card.empId] = false;
      });

      setBulkFlipStates(frontStates);

      // Wait for React to re-render
      await new Promise(resolve => setTimeout(resolve, 150));
      const cards = document.querySelectorAll(".office-id-card-bulk-item");

      if (!cards.length) return;

      const pdf = new jsPDF("p", "mm", "a4");

      const frontX = 8;
      const backX = 108;

      const cardWidth = 62;
      const cardHeight = 92;

      const topMargin = 4;
      const rowGap = 4;

      for (let i = 0; i < cards.length; i++) {

        // Add new page after every 3 employees
        if (i !== 0 && i % 3 === 0) {
          pdf.addPage();
        }

        const row = i % 3;

        const y = topMargin + (row * (cardHeight + rowGap));

        const faces = cards[i].querySelectorAll(".office-id-card-face");

        if (faces.length < 2) continue;

        const frontCanvas = await captureCardElement(faces[0]);
        const backCanvas = await captureCardElement(faces[1]);

        pdf.addImage(
          frontCanvas.toDataURL("image/png"),
          "PNG",
          frontX,
          y,
          cardWidth,
          cardHeight
        );

        pdf.addImage(
          backCanvas.toDataURL("image/png"),
          "PNG",
          backX,
          y,
          cardWidth,
          cardHeight
        );
      }

      pdf.save(
        `Office_ID_Cards_${new Date().toISOString().split("T")[0]}.pdf`
      );

    } catch (err) {
      console.error(err);
      alert("Error exporting PDF");
    } finally {
      setExporting(false);
    }
  };

  const loadBulkCards = async (personIds) => {

    const res = await GetEmployeeCardDetails(personIds.join(","));

    const grouped = {};
    const rows = getEmployeeDetailRows(res);

    rows.forEach(item => {
      grouped[String(item.personID)] = item;
    });

    const cards = personIds.map(id => {

      const emp = dropdownEmployees.find(e => String(e.personID) === String(id));

      const detail = grouped[String(id)];
      const fullAddress = [detail?.add1, detail?.add2]
        .filter(Boolean)
        .join(", ");
      return {

        ...initialData,

        empName: emp?.buddyName || '',
        empId: emp?.personID || id,

        dob: detail?.dob,
        doj: detail?.doj,
        doi: detail?.doi,

        bloodGroup: detail?.bloodGroup,

        empPhotoUrl: getEmployeePhotoUrl(
          detail?.picture,
          id
        ),

        companyAddressFront: fullAddress,
        companyAddressBack: companyAddress,
      };

    });

    setBulkCardData(cards);

    // Initialize flip states for bulk cards (all false = front side)
    const initialFlipStates = {};
    cards.forEach(card => {
      initialFlipStates[card.empId] = false;
    });
    setBulkFlipStates(initialFlipStates);
  };

  const handleExportPDF = () => {
    if (exporting) return;

    mode === "single"
      ? exportSinglePDF()
      : exportBulkPDF();
  };

  // Single mode: stop auto rotation and settle smoothly on the
  // opposite face without causing frame-by-frame React renders.
  const handleSingleFlip = () => {
    if (mode !== 'single' || isManualFlippingRef.current) return;

    stopRotationFrame();
    setIsAutoRotating(false);

    const currentAngle = rotationAngleRef.current;
    const normalizedAngle = ((currentAngle % 360) + 360) % 360;
    const isBackVisible = normalizedAngle >= 90 && normalizedAngle < 270;
    const targetFaceAngle = isBackVisible ? 0 : 180;

    let targetAngle =
      targetFaceAngle +
      Math.ceil((currentAngle - targetFaceAngle) / 360) * 360;

    // Always continue in the same forward direction.
    if (targetAngle <= currentAngle + 0.001) {
      targetAngle += 360;
    }

    rotationAngleRef.current = targetAngle;
    isManualFlippingRef.current = true;

    requestAnimationFrame(() => {
      applyRotation(targetAngle, true);
    });

    manualFlipTimeoutRef.current = setTimeout(() => {
      isManualFlippingRef.current = false;
      manualFlipTimeoutRef.current = null;
    }, MANUAL_FLIP_DURATION);
  };

  const toggleRotation = () => {
    if (mode !== 'single') return;

    if (isAutoRotating) {
      stopRotationFrame();
      setIsAutoRotating(false);
      return;
    }

    clearManualFlipTimer();

    if (flipperRef.current) {
      flipperRef.current.style.transition = 'none';
    }

    lastTimestampRef.current = null;
    setIsAutoRotating(true);
  };

  // Bulk mode: flip individual card
  const handleBulkCardFlip = (empId) => {
    setBulkFlipStates(prev => ({
      ...prev,
      [empId]: !prev[empId]
    }));
  };

  // Bulk mode: flip all cards
  const handleFlipAll = () => {
    if (!bulkCardData.length) return;

    const allFlipped = bulkCardData.every(
      card => bulkFlipStates[card.empId] === true
    );

    const newFlipStates = {};

    bulkCardData.forEach(card => {
      newFlipStates[card.empId] = !allFlipped;
    });

    setBulkFlipStates(newFlipStates);
  };

  const printStyles = `
.office-id-card-scene {
  perspective: 1500px;
  -webkit-perspective: 1500px;
  transform: translateZ(0);
  isolation: isolate;
}

.office-id-card-flipper {
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  will-change: transform;
}

.office-id-card-face {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

@media print {

  @page {
    size: A4 portrait;
    margin: 8mm;
  }

  body * {
    visibility: hidden;
  }

  .office-id-card-page,
  .office-id-card-page *,
  .office-id-card-page__preview,
  .office-id-card-page__preview *,
  .office-id-card-bulk,
  .office-id-card-bulk * {
    visibility: visible;
  }

  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .office-id-card-page {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    background: #fff !important;
  }

  .office-id-card-page__toolbar,
  .office-id-card-page__breadcrumb {
    display: none !important;
  }

  /* ---------- SINGLE ---------- */

  .office-id-card-page__preview {
    display: flex !important;
    justify-content: center !important;
    align-items: flex-start !important;
  }

  /* ---------- BULK ---------- */

  .office-id-card-bulk {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    gap: 6mm !important;
    width: 100% !important;
  }

  .office-id-card-bulk-item {
    display: flex !important;
    justify-content: center !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .office-id-card-bulk-item .office-id-card-scene {
    zoom: .58 !important;
}

  /* ---------- CARD ---------- */

.office-id-card-scene {
    zoom: .58 !important;
    perspective: none !important;
    display: inline-block !important;
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
}

  .office-id-card-flipper {
    display: flex !important;
    flex-direction: row !important;
    justify-content: center !important;
    align-items: flex-start !important;
    gap: 10mm !important;
    width: auto !important;
    height: auto !important;
    transform: none !important;
    transition: none !important;
    transform-style: flat !important;
  }

  .office-id-card-face {
    position: relative !important;
    display: block !important;
    width: 380px !important;
    height: 640px !important;
    transform: none !important;
    transform-origin: top left !important;
    border: 2px solid #081B8E !important;
    box-shadow: none !important;
    overflow: hidden !important;
    backface-visibility: visible !important;
    flex: 0 0 auto !important;
  }

  .office-id-card-face:first-child {
    margin-right: 10mm !important;
  }

}
`;

  const allBulkCardsFlipped =
    bulkCardData.length > 0 &&
    bulkCardData.every(card => bulkFlipStates[card.empId] === true);

  return (
    <>
      <style>{printStyles}</style>
      <Box className="office-id-card-page" sx={{ minHeight: 'calc(100vh - 120px)', p: 3, background: 'linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%)' }}>
        <PageContainer title="Office Employee ID Card">
          <Card sx={{ p: 0, borderRadius: '20px', overflow: 'hidden' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}
              className="office-id-card-page__toolbar"
              sx={{
                backgroundColor: 'primary.light',
                color: 'primary.main',
                borderTopLeftRadius: '20px',
                borderTopRightRadius: '20px',
                borderBottom: '1px solid #e6eef5',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box display="flex" alignItems="center">
                <FeatherIcon icon="credit-card" width="18" />
                <Typography component="span" sx={{ ml: 1, fontWeight: 600 }}>
                  Office Employee ID Card
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">

                {/* Mode Toggle */}
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={mode}
                  color="primary"
                  onChange={async (_, value) => {
                    if (!value) return;

                    setMode(value);
                    setEmployeeSearchTerm("");

                    if (value === "single") {
                      stopRotationFrame();
                      clearManualFlipTimer();

                      setBulkCardData([]);
                      setBulkFlipStates({});

                      rotationAngleRef.current = 0;
                      lastTimestampRef.current = null;

                      setIsAutoRotating(true);

                      await loadLoggedInEmployee();
                    } else {
                      setDesignMode(false);
                      setSelectedDesignElement(null);
                      setSelectedEmployees([String(cardData.empId)]);
                      setBulkFlipStates({
                        [cardData.empId]: false,
                      });
                      stopRotationFrame();
                      clearManualFlipTimer();

                      setIsAutoRotating(false);
                      rotationAngleRef.current = 0;
                      lastTimestampRef.current = null;
                    }
                  }}
                  sx={{
                    height: 40,
                    backgroundColor: '#f0f7ff',
                    borderRadius: '30px',
                    padding: '3px',
                    border: '1px solid #dbeafe',
                    '& .MuiToggleButton-root': {
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      borderRadius: '30px !important',
                      border: 'none',
                      color: '#94a3b8',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'primary.main',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                        }
                      },
                      '&.Mui-selected + &': {
                        borderLeft: 'none',
                      },
                      '&:not(:first-of-type)': {
                        borderLeft: 'none',
                      },
                    }
                  }}
                >
                  <ToggleButton value="single">
                    <Box display="flex" alignItems="center" gap={1}>
                      <FeatherIcon
                        icon="user"
                        size={14}
                        style={{
                          opacity: mode === 'single' ? 1 : 0.6,
                          transition: 'opacity 0.3s ease'
                        }}
                      />
                      Single
                    </Box>
                  </ToggleButton>

                  <ToggleButton value="bulk">
                    <Box display="flex" alignItems="center" gap={1}>
                      <FeatherIcon
                        icon="users"
                        size={14}
                        style={{
                          opacity: mode === 'bulk' ? 1 : 0.6,
                          transition: 'opacity 0.3s ease'
                        }}
                      />
                      Bulk
                    </Box>
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Employees Dropdown with Search */}
                <FormControl
                  size="small"
                  sx={{
                    width: 300,
                    minWidth: 300,
                    maxWidth: 300,
                    flexShrink: 0,
                  }}
                >
                  <InputLabel>Employees</InputLabel>

                  {mode === "single" ? (
                    <Select
                      value={selectedEmployees[0] || ""}
                      label="Employees"
                      disabled={loading}
                      onChange={async (e) => {
                        const personId = e.target.value;
                        setSelectedEmployees([personId]);
                        setEmployeeSearchTerm(''); // Clear search on selection
                        resetSingleRotation();
                        const emp = dropdownEmployees.find(
                          x => String(x.personID) === String(personId)
                        );
                        if (emp) {
                          await loadSelectedEmployee(emp);
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          className: 'employee-dropdown-paper',
                          sx: {
                            maxHeight: '320px !important',
                            overflowY: 'auto !important',
                            overflowX: 'hidden !important',
                          },
                        },
                        disablePortal: false,
                        keepMounted: true,
                      }}
                      renderValue={(selected) => {
                        const emp = dropdownEmployees.find(x => String(x.personID) === String(selected));
                        return emp ? `${emp.buddyName} (${emp.personID})` : '';
                      }}
                    >
                      {/* Search Input - Sticky at top */}
                      <ListSubheader
                        className="employee-search-input"
                        sx={{
                          pointerEvents: 'auto',
                          background: 'white',
                          padding: '8px 16px',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        <TextField
                          inputRef={searchInputRef}
                          size="small"
                          placeholder="Search by name or ID..."
                          value={employeeSearchTerm}
                          onChange={(e) => {
                            setEmployeeSearchTerm(e.target.value);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                          }}
                          onFocus={(e) => {
                            e.stopPropagation();
                          }}
                          fullWidth
                          autoFocus
                          sx={{
                            '& .MuiInputBase-root': {
                              fontSize: '0.875rem',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '8px',
                              '&:hover': {
                                backgroundColor: '#eeeeee',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              }
                            }
                          }}
                        />
                      </ListSubheader>

                      {/* Employee List */}
                      {getFilteredEmployees().length === 0 ? (
                        <MenuItem disabled sx={{ py: 2 }}>
                          No employees found
                        </MenuItem>
                      ) : (
                        getFilteredEmployees().map((emp) => (
                          <MenuItem
                            key={emp.userID}
                            value={String(emp.personID)}
                            sx={{ py: 1 }}
                          >
                            {emp.buddyName} ({emp.personID})
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  ) : (
                    <Select
                      multiple
                      value={selectedEmployees}
                      label="Employees"
                      disabled={loading}
                      onChange={async (e) => {
                        const ids =
                          typeof e.target.value === "string"
                            ? e.target.value.split(",")
                            : e.target.value;
                        setSelectedEmployees(ids);
                        setEmployeeSearchTerm(''); // Clear search on selection
                        setBulkFlipStates({});
                        await loadBulkCards(ids);
                      }}
                      MenuProps={{
                        PaperProps: {
                          className: 'employee-dropdown-paper',
                          sx: {
                            maxHeight: '320px !important',
                            overflowY: 'auto !important',
                            overflowX: 'hidden !important',
                          },
                        },
                        disablePortal: false,
                        keepMounted: true,
                      }}
                      renderValue={(selected) =>
                        dropdownEmployees
                          .filter(emp =>
                            selected.includes(String(emp.personID))
                          )
                          .map(emp => emp.buddyName)
                          .join(", ")
                      }
                    >
                      {/* Search Input - Sticky at top for bulk */}
                      <ListSubheader
                        className="employee-search-input"
                        sx={{
                          pointerEvents: 'auto',
                          background: 'white',
                          padding: '8px 16px',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          borderBottom: '1px solid #e0e0e0',
                        }}
                      >
                        <TextField
                          inputRef={searchInputRef}
                          size="small"
                          placeholder="Search by name or ID..."
                          value={employeeSearchTerm}
                          onChange={(e) => {
                            setEmployeeSearchTerm(e.target.value);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                          }}
                          onFocus={(e) => {
                            e.stopPropagation();
                          }}
                          fullWidth
                          autoFocus
                          sx={{
                            '& .MuiInputBase-root': {
                              fontSize: '0.875rem',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '8px',
                              '&:hover': {
                                backgroundColor: '#eeeeee',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              }
                            }
                          }}
                        />
                      </ListSubheader>

                      {/* Employee List with Checkboxes */}
                      {getFilteredEmployees().length === 0 ? (
                        <MenuItem disabled sx={{ py: 2 }}>
                          No employees found
                        </MenuItem>
                      ) : (
                        getFilteredEmployees().map((emp) => (
                          <MenuItem
                            key={emp.userID}
                            value={String(emp.personID)}
                            sx={{ py: 1 }}
                          >
                            <Checkbox
                              checked={selectedEmployees.includes(
                                String(emp.personID)
                              )}
                            />
                            <ListItemText
                              primary={`${emp.buddyName} (${emp.personID})`}
                            />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  )}
                </FormControl>

                {/* Bulk Mode: Flip controls */}
                {mode === "bulk" && bulkCardData.length > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleFlipAll}
                  >
                    {allBulkCardsFlipped
                      ? 'Flip to Front'
                      : 'Flip All Cards'}
                  </Button>
                )}

                {mode === "single" && (
                  <Button
                    variant={designMode ? "contained" : "outlined"}
                    color="secondary"
                    size="small"
                    onClick={() => {
                      if (designMode) {
                        closeDesignMode();
                      } else {
                        stopRotationFrame();
                        clearManualFlipTimer();
                        setIsAutoRotating(false);
                        setDesignSide("front");
                        setSelectedDesignElement(null);
                        setDesignMode(true);
                      }
                    }}
                    startIcon={<FeatherIcon icon="layout" size={15} />}
                  >
                    {designMode ? "Close Design" : "Design Card"}
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => window.print()}
                >
                  {mode === "bulk" ? "Bulk Print" : "Print Card"}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FeatherIcon icon="download" />}
                  onClick={handleExportPDF}
                  disabled={exporting}
                >
                  {exporting ? "Exporting..." : "Export PDF"}
                </Button>

                {/* Single Mode: Rotation controls */}
                {mode === "single" && (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSingleFlip}
                    >
                      Flip Card
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={toggleRotation}
                      sx={{ minWidth: '120px' }}
                    >
                      {isAutoRotating ? 'Stop Rotation' : 'Start Rotation'}
                    </Button>
                  </>
                )}

              </Box>
            </Box>

            {designMode && mode === "single" && (
              <Box
                sx={{
                  px: 3,
                  pt: 2.5,
                  pb: 2,
                  background: "#f8fbff",
                  borderTop: "1px solid #e6eef5",
                  borderBottom: "1px solid #e6eef5",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: "#123" }}>
                      Card Designer
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select an item on the card, drag it, or change its size below.
                    </Typography>
                  </Box>

                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={designSide}
                    onChange={(_, value) => {
                      if (!value) return;
                      setDesignSide(value);
                      setSelectedDesignElement(null);
                    }}
                  >
                    <ToggleButton value="front">Front</ToggleButton>
                    <ToggleButton value="back">Back</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "240px 1fr 300px" },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      background: "#fff",
                      border: "1px solid #dbe5f0",
                      borderRadius: 2,
                      p: 1,
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    <Typography sx={{ px: 1, py: 0.75, fontSize: 12, fontWeight: 800, color: "#64748b" }}>
                      ELEMENTS
                    </Typography>
                    {Object.entries(cardLayout?.[designSide] || {}).filter(([id]) => id !== 'details' && id !== 'authSign').map(([id]) => (
                      <Button
                        key={id}
                        fullWidth
                        size="small"
                        onClick={() => setSelectedDesignElement(id)}
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          mb: 0.5,
                          borderRadius: 1.5,
                          color: selectedDesignElement === id ? "#fff" : "#334155",
                          backgroundColor: selectedDesignElement === id ? "#1976d2" : "transparent",
                          "&:hover": {
                            backgroundColor: selectedDesignElement === id ? "#1565c0" : "#eef4fb",
                          },
                        }}
                      >
                        {DESIGN_ELEMENT_NAMES[id] || id}
                      </Button>
                    ))}
                  </Box>

                  <Box
                    className="office-id-card-design-stage"
                    sx={{
                      minHeight: 660,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "linear-gradient(145deg,#eef5fc,#e7eef7)",
                      borderRadius: 2,
                      border: "1px solid #d7e2ee",
                      overflow: "auto",
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        transform: "scale(1)",
                        transformOrigin: "center top",
                        p: 4,
                      }}
                    >
                      <Box
                        className="office-id-card-design-canvas"
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setSelectedDesignElement(null);
                        }}
                        sx={{
                          position: "relative",
                          width: 380,
                          height: 640,
                          background: "#fff",
                          boxShadow: "0 18px 45px rgba(15,23,42,.18)",
                        }}
                      >
                        <OfficeIdCard
                          data={cardData}
                          isFlipped={designSide === "back"}
                          frontRef={frontRef}
                          backRef={backRef}
                          layout={cardLayout}
                          editMode={true}
                          selectedId={selectedDesignElement}
                          onSelectElement={(id) => setSelectedDesignElement(id)}
                          onElementDragStart={(event, id) => beginDesignDrag(event, id, "move")}
                          onElementResizeStart={(event, id) => beginDesignDrag(event, id, "resize")}
                          activeDesignSide={designSide}
                          onFlip={() => {}}
                        />
                        {selectedDesignElement && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              pointerEvents: "none",
                              border: "1px dashed rgba(25,118,210,.25)",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      background: "#fff",
                      border: "1px solid #dbe5f0",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                      {selectedDesignElement
                        ? DESIGN_ELEMENT_NAMES[selectedDesignElement] || selectedDesignElement
                        : "Select an element"}
                    </Typography>

                    {getDesignItem() ? (
                      <>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                          }}
                        >
                          {[
                            ["x", "X", 0, 380],
                            ["y", "Y", 0, 640],
                            ["w", "Width", 1, 380],
                            ["h", "Height", 1, 640],
                          ].map(([key, label, min, max]) => (
                            <NumericAdjuster
                              key={key}
                              label={label}
                              value={getDesignItem()[key] ?? 0}
                              min={min}
                              max={max}
                              onChange={(value) =>
                                updateDesignItem(
                                  designSide,
                                  selectedDesignElement,
                                  { [key]: value }
                                )
                              }
                            />
                          ))}
                        </Box>

                        {"fontSize" in getDesignItem() && (
                          <TextField
                            sx={{ mt: 1 }}
                            fullWidth
                            size="small"
                            type="number"
                            label="Font Size"
                            value={getDesignItem().fontSize ?? 16}
                            onChange={(e) =>
                              updateDesignItem(
                                designSide,
                                selectedDesignElement,
                                { fontSize: Math.max(6, Number(e.target.value) || 6) }
                              )
                            }
                          />
                        )}

                        {"opacity" in getDesignItem() && (
                          <TextField
                            sx={{ mt: 1 }}
                            fullWidth
                            size="small"
                            type="number"
                            label="Opacity"
                            value={getDesignItem().opacity ?? 0.12}
                            onChange={(e) =>
                              updateDesignItem(
                                designSide,
                                selectedDesignElement,
                                {
                                  opacity: Math.max(0, Math.min(1, Number(e.target.value) || 0)),
                                }
                              )
                            }
                            inputProps={{ min: 0, max: 1, step: 0.01 }}
                          />
                        )}

                        {designSide === 'front' && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid #dbe5f0',
                            background: '#f8fbff',
                          }}
                        >
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#334155', mb: 1 }}>
                            Details Alignment
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                            Align ADD, D.O.B, D.O.J, D.O.I and Bld Grp as one consistent block.
                          </Typography>

                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            {[
                              ['x', 'Left X', 0, 380],
                              ['w', 'Width', 80, 380],
                              ['startY', 'Start Y', 0, 640],
                              ['rowGap', 'Row Gap', 0, 30],
                              ['labelWidth', 'Label Width', 20, 180],
                              ['colonWidth', 'Colon Width', 4, 40],
                            ].map(([key, label, min, max]) => {
                              const details = getDetailsSettings();
                              return (
                                <NumericAdjuster
                                  key={key}
                                  label={label}
                                  value={details[key] ?? 0}
                                  min={min}
                                  max={max}
                                  onChange={(value) => {
                                    if (['x', 'w', 'startY', 'rowGap'].includes(key)) {
                                      alignDetailRows({ [key]: value });
                                    } else {
                                      updateDetailsSettings({ [key]: value });
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                            <TextField
                              select
                              size="small"
                              label="Labels"
                              value={getDetailsSettings().labelAlign || 'left'}
                              onChange={(e) => updateDetailsSettings({ labelAlign: e.target.value })}
                              SelectProps={{ native: true }}
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </TextField>
                            <TextField
                              select
                              size="small"
                              label="Values"
                              value={getDetailsSettings().valueAlign || 'left'}
                              onChange={(e) => updateDetailsSettings({ valueAlign: e.target.value })}
                              SelectProps={{ native: true }}
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </TextField>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => alignDetailRows()}
                            >
                              Align All Details
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => alignDetailRows({ x: 72, w: 270, startY: 330, rowGap: 0, labelWidth: 80, colonWidth: 12 })}
                            >
                              Reset Alignment
                            </Button>
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const item = getDesignItem();
                              updateDesignItem(designSide, selectedDesignElement, {
                                x: Math.round((380 - item.w) / 2),
                              });
                            }}
                          >
                            Center X
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const item = getDesignItem();
                              updateDesignItem(designSide, selectedDesignElement, {
                                y: Math.round((640 - item.h) / 2),
                              });
                            }}
                          >
                            Center Y
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click any field, logo, photo, signature, or text on the card.
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", gap: 1, mt: 2, pt: 2, borderTop: "1px solid #edf2f7" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={resetCardLayout}
                      >
                        Reset Design
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveCardLayout(cardLayout)}
                      >
                        Save Design
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            <CardContent sx={{ p: 3, background: '#f4f8ff' }}>
              {!designMode && <Box className={mode === 'single' ? 'office-id-card-page__preview' : 'office-id-card-bulk'} sx={{ display: 'flex', justifyContent: 'center', width: '100%' }} >
                {mode === 'single' ? (
                  // Single mode – rotation enabled
                  <Box ref={wrapperRef}>
                    {cardData && (
                      <OfficeIdCard
                        data={cardData}
                        isFlipped={designMode ? designSide === "back" : false}
                        frontRef={frontRef}
                        backRef={backRef}
                        layout={cardLayout}
                        editMode={designMode}
                        selectedId={selectedDesignElement}
                        onSelectElement={setSelectedDesignElement}
                        onElementDragStart={(event, id) => beginDesignDrag(event, id, "move")}
                        onElementResizeStart={(event, id) => beginDesignDrag(event, id, "resize")}
                        onFlip={handleSingleFlip}
                      />
                    )}
                  </Box>
                ) : (
                  // Bulk mode – flip enabled (no rotation)
                  <Box
                    className="office-id-card-bulk"
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: 3,
                      width: "100%",
                      margin: "0 auto",
                    }}
                  >
                    {bulkCardData.map((employee) => (
                      <Box
                        key={employee.empId}
                        className="office-id-card-bulk-item"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          pageBreakInside: "avoid",
                          breakInside: "avoid",
                        }}
                      >
                        <OfficeIdCard
                          data={employee}
                          isFlipped={bulkFlipStates[employee.empId] || false}
                          frontRef={frontRef}
                          backRef={backRef}
                          layout={cardLayout}
                          onFlip={() => handleBulkCardFlip(employee.empId)}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>}
            </CardContent>
          </Card>
        </PageContainer>
      </Box>
    </>
  );
}